import { z } from 'zod';

import { modeSchema } from '@apps/web/lib/mode_store';

// See `libraries/python/organization_events` for the backend schemas.
//
// This module holds only the organization-event wire schemas and their
// inferred types. It must stay a leaf (zod + mode_store only): consumers such
// as `use_listen_for_organization_event` are inside the V3 feature's import
// boundary, which forbids transitively evaluating application modules like
// the `use_organization_events` provider.

const resourceTypeSchema = z.enum([
    'organization',
    'conversation',
    'message',
    'outreach',
    'caregiver',
    'staged_message',
    'shift',
    'shift_confirmation',
    'contact_attempt',
    'callout',
    'agent_conversation',
    'agent_turn',
    'agent_run',
    'voice_call',
    'activity_event',
    'user_settings',
]);

const resourceRefSchema = z.object({
    type: resourceTypeSchema,
    id: z.uuid(),
});

const baseOrganizationEventSchema = z.object({
    timestamp: z.iso.datetime({ offset: true }),
    resource: resourceRefSchema,
    relationships: z.array(resourceRefSchema),
    // Per-org publish sequence; null/absent on user-channel events.
    org_seq: z.number().int().nullable().optional(),
    // Mode of the write that produced the event; absent from older servers.
    mode: modeSchema.nullable().optional(),
});

// The one definition of "this frame carries a sequence" — applied to the raw
// frame so unknown event types and sequence-only suppression stubs count.
export const organizationEventSequenceSchema = z.object({
    org_seq: z.number().int().positive(),
});

const notificationDataSchema = z.object({
    title: z.string().nullable(),
    message: z.string().nullable(),
    in_app_title: z.string().nullable().optional(),
    in_app_message: z.string().nullable().optional(),
    notification_id: z.uuid().nullable().optional(),
    data: z.record(z.string(), z.unknown()).nullable(),
});

const notificationStateUpdateSchema = z.object({
    notification_id: z.uuid(),
    is_read: z.boolean(),
    is_dismissed: z.boolean(),
});

const notificationStateUpdateDataSchema = z.object({
    updates: z.array(notificationStateUpdateSchema),
});

const agentConversationStreamDataSchema = z.object({
    type: z.enum([
        'run_started',
        'reasoning_chunk',
        'response_chunk',
        'response_classified',
        'tool_call_start',
        'tool_call_complete',
        'activity_update',
        'run_complete',
        'run_error',
        'subagent_drafts_started',
        'subagent_drafts_unit_trace',
        'subagent_drafts_unit_complete',
    ]),
    run_id: z.uuid(),
    node_id: z.string(),
    sequence: z.number().int().nonnegative(),
    parent_node_id: z.string().nullable().optional(),
    content: z.string().nullable().optional(),
    phase: z.enum(['thinking', 'tool', 'responding']).nullable().optional(),
    label: z.string().nullable().optional(),
    detail: z.string().nullable().optional(),
    tool_call_id: z.string().nullable().optional(),
    tool_name: z.string().nullable().optional(),
    arguments: z.unknown().nullable().optional(),
    result: z.unknown().nullable().optional(),
    error: z.string().nullable().optional(),
    duration_ms: z.number().int().nonnegative().nullable().optional(),
    response_kind: z.enum(['interstitial', 'final']).nullable().optional(),
});

const organizationEventWithoutPayloadSchema =
    baseOrganizationEventSchema.extend({
        type: z.enum([
            'message_created',
            'conversation_updated',
            'pending_message_updated',
            'contact_attempt_updated',
            'shifts_updated',
            'shift_confirmation_updated',
            'ehr_clock_events_updated',
            'conversations_unread_updated',
            'conversations_mark_all_read',
            'agent_conversation_updated',
            'agent_turn_updated',
            'agent_run_updated',
            'voice_call_updated',
            'outreach_suggestion_added',
            'outreach_suggestion_removed',
            'outreach_updated',
            'agent_automations_updated',
            'activity_event_created',
        ]),
        data: z.null(),
    });

const organizationNotificationEventSchema = baseOrganizationEventSchema.extend({
    type: z.enum([
        'notification_info',
        'notification_success',
        'notification_warning',
        'notification_error',
    ]),
    data: notificationDataSchema,
});

const organizationAgentConversationStreamEventSchema =
    baseOrganizationEventSchema.extend({
        type: z.literal('agent_conversation_stream_event'),
        data: agentConversationStreamDataSchema,
    });

const organizationAgentRunStreamEventSchema =
    baseOrganizationEventSchema.extend({
        type: z.literal('agent_run_stream_event'),
        data: agentConversationStreamDataSchema,
    });

const organizationNotificationStateUpdatedEventSchema =
    baseOrganizationEventSchema.extend({
        type: z.literal('notification_state_updated'),
        data: notificationStateUpdateDataSchema,
    });

const syncProgressDataSchema = z.object({
    sync_log_id: z.uuid(),
    entity_type: z.enum([
        'shifts',
        'caregivers',
        'clients',
        'care_coordinators',
    ]),
    phase: z.enum(['saving', 'finalizing']),
    items_processed: z.number().int().nonnegative().nullable().optional(),
    items_total: z.number().int().nonnegative().nullable().optional(),
    last_updated_at: z.iso.datetime({ offset: true }),
});

const organizationSyncProgressUpdatedEventSchema =
    baseOrganizationEventSchema.extend({
        type: z.literal('sync_progress_updated'),
        data: syncProgressDataSchema,
    });

const chatPresenceDataSchema = z.object({
    run_id: z.uuid(),
    user_id: z.uuid(),
    display_name: z.string(),
    viewing: z.boolean(),
    revision: z.number().int().nonnegative(),
});

const organizationChatPresenceEventSchema = baseOrganizationEventSchema.extend({
    type: z.literal('chat_presence'),
    data: chatPresenceDataSchema,
});

const chatTypingDataSchema = z.object({
    run_id: z.uuid(),
    user_id: z.uuid(),
    display_name: z.string(),
    typing: z.boolean(),
});

const organizationChatTypingEventSchema = baseOrganizationEventSchema.extend({
    type: z.literal('chat_typing'),
    data: chatTypingDataSchema,
});

const conversationTypingDataSchema = z.object({
    conversation_id: z.uuid(),
    user_id: z.uuid(),
    display_name: z.string(),
    typing: z.boolean(),
});

const organizationConversationTypingEventSchema =
    baseOrganizationEventSchema.extend({
        type: z.literal('conversation_typing'),
        data: conversationTypingDataSchema,
    });

const incomingCallDataSchema = z.object({
    call_id: z.uuid(),
    organization_id: z.uuid(),
    from_number_display: z.string(),
    caller_label: z.string().nullable(),
    line_label: z.string().nullable(),
    ring_started_at: z.iso.datetime({ offset: true }),
});

const organizationIncomingCallEventSchema = baseOrganizationEventSchema.extend({
    type: z.literal('incoming_call'),
    data: incomingCallDataSchema,
});

export const organizationEventSchema = z.discriminatedUnion('type', [
    organizationEventWithoutPayloadSchema,
    organizationNotificationEventSchema,
    organizationNotificationStateUpdatedEventSchema,
    organizationAgentConversationStreamEventSchema,
    organizationAgentRunStreamEventSchema,
    organizationSyncProgressUpdatedEventSchema,
    organizationChatPresenceEventSchema,
    organizationChatTypingEventSchema,
    organizationConversationTypingEventSchema,
    organizationIncomingCallEventSchema,
]);

export type OrganizationEventType = OrganizationEvent['type'];

export type OrganizationEvent = z.infer<typeof organizationEventSchema>;

// Completeness-checked registry of every event type this build understands.
// Typed as Record<OrganizationEventType, true> so adding a union member without
// listing it here is a compile error.
const KNOWN_ORGANIZATION_EVENT_TYPES: Record<OrganizationEventType, true> = {
    message_created: true,
    conversation_updated: true,
    pending_message_updated: true,
    contact_attempt_updated: true,
    shifts_updated: true,
    shift_confirmation_updated: true,
    ehr_clock_events_updated: true,
    conversations_unread_updated: true,
    conversations_mark_all_read: true,
    agent_conversation_updated: true,
    agent_turn_updated: true,
    agent_run_updated: true,
    voice_call_updated: true,
    outreach_suggestion_added: true,
    outreach_suggestion_removed: true,
    outreach_updated: true,
    agent_automations_updated: true,
    activity_event_created: true,
    notification_info: true,
    notification_success: true,
    notification_warning: true,
    notification_error: true,
    notification_state_updated: true,
    agent_conversation_stream_event: true,
    agent_run_stream_event: true,
    sync_progress_updated: true,
    chat_presence: true,
    chat_typing: true,
    conversation_typing: true,
    incoming_call: true,
};

export function isKnownOrganizationEventType(rawData: unknown): boolean {
    if (typeof rawData !== 'object' || rawData === null) {
        return false;
    }
    const type = (rawData as { readonly type?: unknown }).type;
    return (
        typeof type === 'string' &&
        Object.prototype.hasOwnProperty.call(
            KNOWN_ORGANIZATION_EVENT_TYPES,
            type,
        )
    );
}
